import React from 'react';
import { Link } from 'react-router-dom';
import ZitoFooter from '../components/ZitoFooter';

/**
 * /billing-policy — customer-facing billing, refund, and cancellation
 * policy for AgentOmatic.io subscriptions sold by Zitotron Tech LLC.
 * Mirrors the portal's /legal/billing page verbatim so Stripe
 * reviewers see one consistent policy across both public domains.
 */

const BillingPolicyPage: React.FC = () => {
  const lastUpdated = 'April 14, 2026';
  return (
    <div className="App" style={{ minHeight: '100vh' }}>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '56px 24px 48px' }}>

        <div style={{ marginBottom: 20 }}>
          <Link to="/" style={{ color: '#6d6880', textDecoration: 'none', fontSize: 12, fontWeight: 600 }}>
            ← Home
          </Link>
        </div>

        <h1 style={{
          margin: '0 0 8px', fontSize: 'clamp(1.8rem, 4vw, 2.4rem)', fontWeight: 800,
          letterSpacing: -0.4, color: '#e8e6f0',
        }}>Billing, Refund &amp; Cancellation Policy</h1>
        <p style={{ color: '#6d6880', fontSize: 13, marginBottom: 30 }}>
          Last updated: {lastUpdated} · Applies to all AgentOmatic.io subscriptions sold by Zitotron Tech LLC.
        </p>

        <Section title="1. What we sell">
          AgentOmatic.io is a software subscription. Paid tiers (Solo, Founder, Startup, Enterprise) are billed on a recurring basis — <strong>monthly</strong> or <strong>annual</strong>, per seat — through Stripe, Inc. A free Community tier is also available with no payment. All prices are listed in USD.
        </Section>

        <Section title="2. Billing">
          Your selected plan is charged immediately upon signup and automatically renewed at the end of each billing period (monthly or annually, depending on plan) until you cancel or downgrade. Receipts are emailed at each successful charge. Taxes (where applicable) are calculated by Stripe at checkout.
        </Section>

        <Section title="3. Refund policy — no refunds on subscriptions">
          <p>Subscription charges are <strong>non-refundable</strong>. This includes:</p>
          <ul style={listStyle}>
            <li>Partially used billing periods.</li>
            <li>Mid-period downgrades or cancellations.</li>
            <li>Accounts that become inactive, unused, or are abandoned after purchase.</li>
            <li>Annual plans that are cancelled before the 12-month term ends.</li>
          </ul>
          <p>If you believe you were charged in error (e.g. a duplicate charge or a failed delivery), email <a href="mailto:support@zitotrontech.com" style={linkStyle}>support@zitotrontech.com</a> within 30 days of the charge and we will investigate. Bona-fide billing errors are corrected on a case-by-case basis.</p>
        </Section>

        <Section title="4. Cancellation">
          <p>You can cancel your subscription at any time from your account page on{' '}
            <a href="https://agentomatic.io/account" style={linkStyle}>AgentOmatic.io/account</a>, or by emailing{' '}
            <a href="mailto:support@zitotrontech.com" style={linkStyle}>support@zitotrontech.com</a>.
          </p>
          <p><strong>When you cancel:</strong></p>
          <ul style={listStyle}>
            <li>You keep full access to your current tier's features through the end of the billing period you already paid for.</li>
            <li>At the end of that period, the subscription ends and you are <strong>not billed again</strong>.</li>
            <li>Your account is preserved and automatically downgrades to the free Community tier. You can reactivate a paid plan anytime.</li>
            <li>No prorated refund is issued for the remainder of the current period.</li>
          </ul>
        </Section>

        <Section title="5. Downgrades to a lower paid tier">
          Downgrades behave the same way as cancellations: the change takes effect at the <strong>end of the current billing period</strong> and no refund or proration is issued. You keep your higher-tier features until the period ends, then the account automatically switches to the target tier and billing continues at the new price. Scheduled downgrades can be cancelled from the account page any time before they take effect.
        </Section>

        <Section title="6. Upgrades to a higher paid tier">
          Upgrades take effect immediately. You are charged the new tier's price and your previous plan's unused balance is credited toward the new charge on a pro-rated basis by Stripe. The new tier's features unlock as soon as Stripe confirms the payment.
        </Section>

        <Section title="7. Failed payments">
          If a renewal payment fails (e.g. expired card), Stripe will retry the charge automatically for up to 21 days. During that window your access continues uninterrupted. If all retries fail, your account downgrades to the Community tier and you can re-enter payment details from your account page to restore the paid plan.
        </Section>

        <Section title="8. Chargebacks &amp; disputes">
          We strongly prefer you contact us before filing a chargeback — email <a href="mailto:support@zitotrontech.com" style={linkStyle}>support@zitotrontech.com</a> and most issues are resolved within one business day. Chargebacks filed without prior contact may result in immediate account suspension pending resolution with your card issuer.
        </Section>

        <Section title="9. Enterprise contracts">
          Enterprise-tier customers are billed under the terms of a signed contract with Zitotron Tech LLC, which may differ from the terms above. Enterprise contract terms govern when they differ from this policy. Contact <a href="mailto:sales@zitotrontech.com" style={linkStyle}>sales@zitotrontech.com</a>.
        </Section>

        <Section title="10. Contact">
          <div style={{
            padding: '14px 16px',
            background: 'rgba(124,108,240,0.08)',
            border: '1px solid rgba(124,108,240,0.30)',
            borderRadius: 10,
            fontSize: 13.5, lineHeight: 1.8, color: '#a8a4bc',
          }}>
            <div><strong style={{ color: '#e8e6f0' }}>Zitotron Tech LLC</strong></div>
            <div>30 N Gould St, Ste N · Sheridan, WY 82801 · USA (registered)</div>
            <div>Operations: Portland, Oregon, USA</div>
            <div>Support: <a href="mailto:support@zitotrontech.com" style={linkStyle}>support@zitotrontech.com</a></div>
            <div>Sales: <a href="mailto:sales@zitotrontech.com" style={linkStyle}>sales@zitotrontech.com</a></div>
            <div>Phone: <a href="tel:+13072178966" style={linkStyle}>+1 (307) 217-8966</a> (Mon–Fri, 9am–6pm PT)</div>
          </div>
        </Section>
      </div>
      <ZitoFooter />
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section style={{ marginBottom: 28 }}>
    <h2 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 10px', color: '#e8e6f0' }}>{title}</h2>
    <div style={{ color: '#a8a4bc', fontSize: 14.5, lineHeight: 1.7 }}>{children}</div>
  </section>
);

const listStyle: React.CSSProperties = { margin: '8px 0 12px 22px', lineHeight: 1.8 };
const linkStyle: React.CSSProperties = { color: '#a78bfa', textDecoration: 'none', borderBottom: '1px solid rgba(167,139,250,0.55)' };

export default BillingPolicyPage;
